// @givmo/app-admin — the "Company donations" history table on `/give` (EP-WEB14).
//
// The company's own DIRECT gifts (EP-WEB2), rendered as the LEDGER half of the compose-plus-ledger
// `/give` screen (EP-DES1 §2): what you are about to give, above what you already gave. It used to be
// a data-driven section of the dashboard; it moved here as a COMPONENT with its whole `data-testid`
// vocabulary intact — `dashboard-company-gave` included. A testid is a wire contract pinned by a
// Playwright spec, so renaming one to match its new address would turn a one-line navigation change in
// e2e into a selector rewrite. The stale-sounding prefix is the cheap side of that trade, exactly as
// `dashboard-approvals` kept its name when the queue moved to `/approvals`.
//
// READ-ONLY BY DESIGN: a direct company donation is money the company ALREADY moved — there are no
// approval semantics, so no actions column, no kebab, and no Owner gating (nothing to gate). These rows
// never join the pending count, the Approve-all set, or the grouped machinery. An e2e spec pins the
// invariant as `companyGave.locator('button')).toHaveCount(0)`, so the toolbar and the row cells stay
// button-free; the error state's retry is the one control here and is mutually exclusive with rows.
//
// STATES ARE FIRST-CLASS HERE, unlike on the dashboard. There the page rendered the table only once the
// wire carried direct rows, so loading/empty/error never had to exist. On its own page a blank gap would
// read as a broken screen, so all four states render through the DataTable's own slots.
//
// GATING: the section rides `/give`'s existing `VITE_COMPANY_DIRECT_GIVING_ENABLED` gate and holds no
// switch of its own — the page returns null while dark, so the history goes dark with the compose flow.

import {
  Avatar,
  Badge,
  DataTable,
  EmptyState,
  Icon,
  type DataTableColumn,
} from '@givmo/design-system';
import { HeartHandshake } from 'lucide-react';
import { companyGaveTarget, type CompanyGaveRow } from '../dashboard/dashboard-decode';
import { CharityCell } from '../dashboard/sections/charity-cell';
import { errorMessage } from '../dashboard/sections/section-status';

// ── "Company gave" target cell (EP-WEB2): the gift's recipient. A single-charity gift reuses the
//    shared CharityCell; a grouped (ETF/fundraiser) gift renders its group name + kind badge. The
//    wire's collapsed direct row carries no per-split charity list, so (unlike GroupCell) there is
//    no count badge or tooltip — the representative charity name is the unnamed-group fallback. ────
function CompanyGaveTargetCell({ row }: { row: CompanyGaveRow }) {
  if (row.kind === 'single' && row.charity !== null) {
    return <CharityCell charity={row.charity} />;
  }
  const title = companyGaveTarget(row);
  return (
    <div className="flex min-w-0 items-center gap-10">
      <Avatar name={title} size="sm" />
      <div className="flex min-w-0 flex-col items-start gap-3">
        <span className="truncate font-w600 text-text-strong">{title}</span>
        {row.groupKindLabel !== null ? (
          <Badge tone="neutral" size="sm">
            {row.groupKindLabel}
          </Badge>
        ) : null}
      </div>
    </div>
  );
}

// Static (no closures): the column set never changes.
const COMPANY_GAVE_COLUMNS: DataTableColumn<CompanyGaveRow>[] = [
  {
    id: 'charity',
    header: 'Charity',
    cell: (r) => <CompanyGaveTargetCell row={r} />,
  },
  {
    id: 'amount',
    header: 'Amount',
    cell: (r) => r.amount,
    align: 'right',
    numeric: true,
    width: '120px',
  },
  {
    id: 'date',
    header: 'Date',
    cell: (r) => <span className="text-text-tertiary">{r.gaveAt}</span>,
    width: '120px',
  },
  {
    id: 'status',
    header: 'Status',
    // The company-attribution chip — the row's "status" is that the company already gave.
    cell: (r) => (
      <Badge tone="success" dot testId={`company-gave-badge-${r.id}`}>
        Company gave
      </Badge>
    ),
    width: '150px',
  },
];

export function GiveHistoryTable({
  rows,
  loading,
  isError,
  error,
  onRetry,
}: {
  rows: CompanyGaveRow[];
  loading: boolean;
  isError: boolean;
  error: unknown;
  onRetry: () => void;
}) {
  return (
    <DataTable
      testId="dashboard-company-gave"
      density="comfortable"
      columns={COMPANY_GAVE_COLUMNS}
      rows={rows}
      rowKey={(r) => r.id}
      rowTestId={(r) => `company-gave-row-${r.id}`}
      loading={loading}
      loadingTestId="company-gave-loading"
      error={isError ? errorMessage(error, 'Failed to load company donations.') : undefined}
      onRetry={onRetry}
      errorTestId="company-gave-error"
      retryTestId="company-gave-error-retry"
      toolbar={
        <div className="flex items-center gap-8">
          <h3 className="font-sans text-s15 font-w600 tracking-givmo text-text-strong">
            Company donations
          </h3>
          {/* Only once the read has SETTLED successfully: "0 given" beside a skeleton or an error
              states a fact we do not have. */}
          {!loading && !isError && <Badge tone="success">{rows.length} given</Badge>}
        </div>
      }
      emptyState={
        <EmptyState
          testId="company-gave-empty-state"
          icon={<Icon icon={HeartHandshake} size={20} aria-hidden />}
          title="No company donations yet"
          description="Gifts your company makes from its own funds will appear here."
        />
      }
    />
  );
}
